import React, { useCallback, useEffect } from "react";
import { useMusicApi } from "../../api/useMusicApi";
import { useLoading } from "../../hooks/use-loading";
import { Card } from "../../components/Card/Card";
import { CardBody } from "../../components/Card/CardBody";
import { Table } from "../../components/Table/Table";
import { albumsColumnsDefinition } from "./AlbumsColumnsDefinition";
import { CardContentPreloader } from "../../components/Card/CardContentPreloader";
import { AlbumsFilters } from "./AlbumsFilters";
import { PaginationComponent } from "../../components/Pagination";
import { useTableSelect } from "../../hooks/use-table-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons/faSpinner";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { useModalConfirmAction } from "../../components/Modals/ModalConfirmActionContext";
import { toast } from "react-toastify";
import { useApiExecutor } from "../../hooks/useApiExecutor";
import { usePageHelper } from "../../hooks/use-page-helper";

export enum AlbumType {
  AUDIO = "audio",
  VIDEO = "video",
}

type Props = {
  type: AlbumType;
  onChange: (totalCount: number | string, currentCount: number) => void;
};

export const Albums: React.FC<Props> = ({ onChange }) => {
  const api = useMusicApi();
  const { t } = usePageHelper("albums");
  const { contents, info, filters, fetch, fetchPage } = useApiExecutor({
    fetchCallback: api.getAlbums,
    showInPage: 50,
  });

    const { selectedContents, handleRowSelect } = useTableSelect(
      "id",
      contents,
    );
  const { showConfirmActionModal } = useModalConfirmAction();
  const { startLoading, stopLoading, loadings } = useLoading({
    updateDate: false,
    available: false,
    takedown: false,
  });

  useEffect(() => {
    onChange(info.allContentCounts ?? 0, info.contentsCountWithOffset);
  }, [info?.allContentCounts, info?.contentsCountWithOffset]);

  const handleTakeDownAlbum = useCallback(async () => {
    try {
      if (
        !(await showConfirmActionModal(
          `Вы уверены, что хотите изъять контент (${selectedContents.length} шт.) со всех площадок?`,
        ))
      ) {
        return;
      }
      startLoading("takedown");
      await api.putTeardownAlbums(selectedContents);
      toast.success("Изъятие альбома успешно запущено");
    } catch (e) {
    } finally {
      stopLoading("takedown");
    }
  }, [selectedContents]);

  return (
    <>
      <div className={"flex justify-between items-center flex-wrap mb-3 gap-2"}>
        <AlbumsFilters onApply={fetch} />
        <div>
          <button
            type="button"
            disabled={selectedContents.length === 0}
            className="inline-flex items-center px-3 py-2.5 text-xs dark:text-white font-medium text-gray-900 bg-transparent border border-gray-900 rounded-r-md disabled:opacity-50 cursor-pointer hover:bg-gray-900 focus:z-10 focus:ring-2 focus:ring-gray-500 focus:bg-gray-900"
            onClick={handleTakeDownAlbum}
          >
            <FontAwesomeIcon
              icon={loadings.takedown ? faSpinner : faTrash}
              spin={loadings.takedown}
              className="w-3 h-3 mr-2 fill-current"
            />
            {t("revoke")}
          </button>
        </div>
      </div>
      <Card>
        <CardBody>
          {info.isInitialLoading || info.isPageLoading ? (
            <CardContentPreloader />
          ) : (
            <Table
              data={contents}
              columnsDefinition={albumsColumnsDefinition(
                t,
                filters?.content_type ?? "audio",
              )}
              onRowSelectionChange={handleRowSelect}
            />
          )}
        </CardBody>
      </Card>
      <br />
      {!info.isPageLoading && !info.isInitialLoading && (
        <PaginationComponent threshold={10} onChange={fetchPage} info={info} />
      )}
    </>
  );
};
